import React from 'react'
import { Avatar } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { Flex, Text } from '@chakra-ui/react'

import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    namedOperations,
    useCreateInternalChatUserMutation,
    useDeleteInternalChatUserMutation,
    UserFieldsFragment,
} from '~graphql/generated/graphql'
import { getUserFullName } from '~utils/helpers'
import { useTeamChatConversation } from '../../provider'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { navigate } from 'gatsby'
import { UserRoutes } from '~config/routes'

interface Props {
    user: Pick<
        UserFieldsFragment,
        'id' | 'first_name' | 'last_name' | 'avatar_url'
    >
    internalChatUserId?: string
}

export default function InternalChatMemberDetails({
    user,
    internalChatUserId,
}: Props) {
    const { internalChat, currentInternalChatUser } = useTeamChatConversation()
    const isAdded = !!internalChatUserId
    const isCurrentUser = currentInternalChatUser?.user.id === user.id

    const { onCompleted: onCompletedCreate, onError: onErrorCreate } =
        useToastFeedback(
            'Successfully added user to chat.',
            'Error adding user to chat.',
        )
    const { onCompleted: onCompletedDelete, onError: onErrorDelete } =
        useToastFeedback(
            'Successfully removed user from chat.',
            'Error removing user from chat.',
        )

    const refetchQueries = [namedOperations.Query.internalChatUsers]

    const [createInternalChatUser, { loading: createLoading }] =
        useCreateInternalChatUserMutation({
            onCompleted: data => {
                if (data.createInternalChatUser) {
                    onCompletedCreate()
                } else {
                    onErrorCreate()
                }
            },
            onError: onErrorCreate,
            refetchQueries,
        })
    const [deleteInternalChatUser, { loading: deleteLoading }] =
        useDeleteInternalChatUserMutation({
            onCompleted: () => {
                onCompletedDelete()
            },
            onError: onErrorDelete,
            refetchQueries,
        })

    const loading = createLoading || deleteLoading

    async function handleAddUser() {
        if (loading || internalChatUserId) return

        try {
            await createInternalChatUser({
                variables: {
                    chatId: internalChat.id,
                    userId: user.id,
                },
            })
        } catch (error) {
            console.error('Error adding user to chat:', error)
        }
    }

    async function handleRemoveUser() {
        if (loading || !internalChatUserId) return

        try {
            await deleteInternalChatUser({
                variables: {
                    id: internalChatUserId || '',
                },
                onCompleted: () => {
                    if (isCurrentUser) {
                        onCompletedDelete()
                        navigate(UserRoutes.TeamChat)
                    } else {
                        onCompletedDelete()
                    }
                },
            })
        } catch (error) {
            console.error('Error removing user from chat:', error)
        }
    }

    return (
        <Flex
            w="full"
            p={2}
            justify="space-between"
            align="center"
            rounded="lg"
            _hover={{ bg: 'gray.100' }}
        >
            <SharedStack row fit spacing={3}>
                <Avatar
                    borderRadius="md"
                    size="sm"
                    src={user.avatar_url || ''}
                    name={getUserFullName(user)}
                />
                <Text fontSize="sm" fontWeight="semibold">
                    {getUserFullName(user)}
                    {isCurrentUser ? ' (You)' : ''}
                </Text>
            </SharedStack>
            {isAdded ? (
                <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={handleRemoveUser}
                    isLoading={loading}
                    loadingText="Removing..."
                >
                    {isCurrentUser ? 'Leave' : 'Remove'}
                </Button>
            ) : (
                <Button
                    size="xs"
                    variant="solid"
                    colorScheme="brand"
                    onClick={handleAddUser}
                    isLoading={loading}
                    loadingText="Adding..."
                >
                    Add
                </Button>
            )}
        </Flex>
    )
}
